import {
  FormFieldType,
  LeadFormField,
} from '../../types/leadForms';

interface Props {
  field: LeadFormField;
  accentColor?: string;
  value?: unknown;
  onChange?: (value: unknown) => void;
}

const base =
  'w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700';

export function FormFieldRenderer({
  field,
  accentColor = '#be123c',
  value,
  onChange,
}: Props) {
  const options = field.options ?? [];

  const label = (
    <label className="mb-2 block text-sm font-semibold text-slate-700">
      {field.label}
      {field.required && (
        <span className="text-red-600"> *</span>
      )}
    </label>
  );

  const help = field.helpText ? (
    <p className="mt-2 text-xs text-slate-500">
      {field.helpText}
    </p>
  ) : null;

  if (field.type === FormFieldType.Checkbox) {
    return (
      <div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) =>
              onChange?.(e.target.checked)
            }
            style={{ accentColor }}
          />

          <span>{field.label}</span>
        </label>

        {help}
      </div>
    );
  }

  if (field.type === FormFieldType.CheckboxGroup) {
    const selected =
      (value as string[]) ?? [];

    return (
      <div>
        {label}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {options.map((option) => (
            <label
              key={option}
              className="flex items-start gap-1 text-sm text-slate-700"
            >
              <input
                type="checkbox"
                checked={selected.includes(
                  option
                )}
                onChange={(e) => {
                  const updated =
                    e.target.checked
                      ? [
                          ...selected,
                          option,
                        ]
                      : selected.filter(
                          (item) =>
                            item !== option
                        );

                  onChange?.(updated);
                }}
                style={{ accentColor }}
              />

              <span className="break-words leading-5">
                {option}
              </span>
            </label>
          ))}
        </div>

        {help}
      </div>
    );
  }

  if (field.type === FormFieldType.RadioGroup) {
    return (
      <div>
        {label}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {options.map((option) => (
            <label
              key={option}
              className="flex items-start gap-2 text-sm text-slate-700"
            >
              <input
                type="radio"
                name={field.id}
                checked={value === option}
                onChange={() =>
                  onChange?.(option)
                }
                style={{ accentColor }}
              />

              <span className="break-words leading-5">
                {option}
              </span>
            </label>
          ))}
        </div>

        {help}
      </div>
    );
  }

  if (field.type === FormFieldType.Dropdown) {
    return (
      <div>
        {label}

        <select
          className={base}
          style={{ borderColor: accentColor }}
          value={(value as string) ?? ''}
          onChange={(e) =>
            onChange?.(e.target.value)
          }
        >
          <option value="">
            Select an option
          </option>

          {options.map((option) => (
            <option
              key={option}
              value={option}
            >
              {option}
            </option>
          ))}
        </select>

        {help}
      </div>
    );
  }

  if (field.type === FormFieldType.TextArea) {
    return (
      <div>
        {label}

        <textarea
          rows={4}
          className={base}
          style={{ borderColor: accentColor }}
          placeholder={field.placeholder}
          value={(value as string) ?? ''}
          onChange={(e) =>
            onChange?.(e.target.value)
          }
        />

        {help}
      </div>
    );
  }

  const inputType =
    field.type === FormFieldType.Email
      ? 'email'
      : field.type === FormFieldType.Phone
      ? 'tel'
      : field.type === FormFieldType.Number
      ? 'number'
      : field.type === FormFieldType.Date
      ? 'date'
      : 'text';

  return (
    <div>
      {label}

      <input
        type={inputType}
        className={base}
        style={{ borderColor: accentColor }}
        placeholder={field.placeholder}
        value={(value as string) ?? ''}
        onChange={(e) =>
          onChange?.(e.target.value)
        }
      />

      {help}
    </div>
  );
}

export const formFieldTypes = [
  { value: FormFieldType.Text, label: 'Text' },
  { value: FormFieldType.Email, label: 'Email' },
  { value: FormFieldType.Phone, label: 'Phone' },
  { value: FormFieldType.Number, label: 'Number' },
  { value: FormFieldType.TextArea, label: 'Text Area' },
  { value: FormFieldType.Checkbox, label: 'Checkbox' },
  { value: FormFieldType.CheckboxGroup, label: 'Checkbox Group' },
  { value: FormFieldType.RadioGroup, label: 'Radio Group' },
  { value: FormFieldType.Dropdown, label: 'Dropdown' },
  { value: FormFieldType.Date, label: 'Date' },
];